import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Animated as ReactAnimated,
  FlatList,
  Dimensions,
} from 'react-native';
import Animated, {
  Easing,
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { isTablet } from 'react-native-device-info';
import { EventRegister } from 'react-native-event-listeners';
import AppContext from '../../navigation/Context';
import colors from '../../resources/colors';
import images from '../../resources/images';
import { useToast } from 'react-native-toast-notifications';
import { format } from 'date-fns';
import { useNavigation } from '@react-navigation/core';
import DataController from '../../controllers/DataController';
import FirebaseService from '../../controllers/FirebaseService';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { createStackNavigator } from '@react-navigation/stack';

const UncheckedIcon = () => (
  <View style={styles.uncheckedIconContainer}>
    <View style={styles.uncheckedIcon} />
  </View>
);

const Checkbox = ({ text, checked, checkboxPressed }) => {
  const [isChecked, setIsChecked] = useState(checked);

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  return (
    <View>
      <TouchableOpacity
        activeOpacity={0.5}
        disabled={isChecked}
        style={styles.checkboxButton}
        onPress={() => checkboxPressed()}>
        <Text style={styles.checkboxText} allowFontScaling={false}>
          {text}
        </Text>
        {isChecked ? (
          <Image
            style={styles.checkboxIcon}
            source={images.Button_Checked}
          />
        ) : (
          <UncheckedIcon />
        )}
      </TouchableOpacity>
      <View style={styles.checkboxSeparator} />
    </View>
  )
};

const FontController = ({ }) => {
  const [fontMultiplier, setFontMultiplier] = useState(1);

  useEffect(() => {
    getFontMultiplier();
  }, []);

  const getFontMultiplier = async () => {
    const fontMultiplierInStorage = await DataController.getFontMultiplier();
    setFontMultiplier(fontMultiplierInStorage);
  };

  const changeFontMultiplier = async (addToMultiplier) => {
    const newMultiplier = fontMultiplier + addToMultiplier;

    if (newMultiplier < 3.1 && newMultiplier > 0.5) {
      !__DEV__ && FirebaseService.logEvent('font_size_change', { screen: 'Settings', change: newMultiplier });
      setFontMultiplier(newMultiplier);
      await DataController.setFontMultiplier(newMultiplier);
    }
  }

  const resetFontMultiplier = async () => {
    !__DEV__ && FirebaseService.logEvent('font_size_change', { screen: 'Settings', change: 1, reset: true });
    setFontMultiplier(1);
    await DataController.setFontMultiplier(1);
  }

  return (
    <View>
      <Text style={styles.header}>
        {'Adjust font size'}
      </Text>
      <View style={styles.fontOuterContainer}>
        <View style={styles.fontContainer}>
          <TouchableOpacity
            activeOpacity={0.5}
            style={styles.fontButton}
            onPress={() => changeFontMultiplier(-0.1)}>
            <Image style={styles.fontIcon} source={images.Button_Decrease} />
          </TouchableOpacity>
          <View style={styles.fontSizeContainer}>
            <Text style={styles.fontSizeText} allowFontScaling={false}>
              {`${(fontMultiplier * 100).toFixed(0)}%`}
            </Text>
          </View>
          <TouchableOpacity
            activeOpacity={0.5}
            style={styles.fontButton}
            onPress={() => changeFontMultiplier(0.1)}>
            <Image style={styles.fontIcon} source={images.Button_Increase} />
          </TouchableOpacity>
        </View>
        {(fontMultiplier * 100).toFixed(0) !== '100' && (
          <View style={styles.resetButtonContainer}>
            <TouchableOpacity style={styles.resetButton} onPress={() => resetFontMultiplier()}>
              <Text style={styles.resetButtonText} allowFontScaling={false}>
                {'Reset'}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      <Text style={styles.header}>
        {'Preview'}
      </Text>
      <View style={styles.textPreviewContainer}>
        <Text style={[styles.textPreview, {
          fontSize: 16 * fontMultiplier,
          lineHeight: 22 * fontMultiplier,
        }]}>
          {'This is a sample of how the standard text will look when displaying Lectionary for a day'}
        </Text>
      </View>
    </View>
  );
};

const ServiceType = ({ serviceType }) => {
  const [checkboxSelected, setCheckboxSelected] = useState([true, false]);

  useEffect(() => {
    const getServiceType = async () => {
      const type = await DataController.getServiceType();

      if (type === 1) {
        setCheckboxSelected([false, true]);
      }
    };

    getServiceType();
  }, []);

  const checkboxPressed = async (index) => {
    if (index === 0) {
      !__DEV__ && FirebaseService.logEvent('service_sheet_type_changed', { screen: 'Settings', type: 'Continuous (default)' });
      setCheckboxSelected([true, false]);
    } else if (index === 1) {
      !__DEV__ && FirebaseService.logEvent('service_sheet_type_changed', { screen: 'Settings', type: 'Related' });
      setCheckboxSelected([false, true]);
    }

    await DataController.saveServiceType(index);
  };

  return (
    <View style={styles.settingContainer}>
      <Text style={styles.header}>
        {'Select the default Service Sheet type'}
      </Text>
      <Checkbox
        checked={checkboxSelected[0]}
        text={'Continuous (default)'}
        checkboxPressed={() => checkboxPressed(0)}
      />
      <Checkbox
        checked={checkboxSelected[1]}
        text={'Related'}
        checkboxPressed={() => checkboxPressed(1)}
      />
    </View>
  );
};

const CollectType = ({ }) => {
  const [checkboxSelected, setCheckboxSelected] = useState([true, false]);

  useEffect(() => {
    const getCollectType = async () => {
      const type = await DataController.getCollectType();

      if (type === 1) {
        setCheckboxSelected([false, true]);
      }
    };

    getCollectType();
  }, []);

  const checkboxPressed = async (index) => {
    if (index === 0) {
      !__DEV__ && FirebaseService.logEvent('app_start_behaviour', { type: 'Home' });
      setCheckboxSelected([true, false]);
    } else if (index === 1) {
      !__DEV__ && FirebaseService.logEvent('app_start_behaviour', { type: "Today's Lectionary" });
      setCheckboxSelected([false, true]);
    }

    await DataController.saveCollectType(index);
  };

  return (
    <View style={styles.settingContainer}>
      <Text style={styles.header}>
        {'Select the default Collect'}
      </Text>
      <Checkbox
        checked={checkboxSelected[0]}
        text={'Collect 1 (default)'}
        checkboxPressed={() => checkboxPressed(0)}
      />
      <Checkbox
        checked={checkboxSelected[1]}
        text={'Collect 2'}
        checkboxPressed={() => checkboxPressed(1)}
      />
    </View>
  );
};

const OpeningType = ({ }) => {
  const [checkboxSelected, setCheckboxSelected] = useState([true, false]);

  useEffect(() => {
    const getAppOpenBehaviour = async () => {
      const type = await DataController.getAppOpenBehaviour();

      if (type === 1) {
        setCheckboxSelected([false, true]);
      }
    };

    getAppOpenBehaviour();
  }, []);

  const checkboxPressed = async (index) => {
    if (index === 0) {
      setCheckboxSelected([true, false]);
    } else if (index === 1) {
      setCheckboxSelected([false, true]);
    }

    await DataController.saveAppOpenBehaviour(index);
  };

  return (
    <View style={styles.settingContainer}>
      <Text style={styles.header}>
        {'Select what appears when opening the app'}
      </Text>
      <Checkbox
        checked={checkboxSelected[0]}
        text={'Home (default)'}
        checkboxPressed={() => checkboxPressed(0)}
      />
      <Checkbox
        checked={checkboxSelected[1]}
        text={'Today’s Lectionary'}
        checkboxPressed={() => checkboxPressed(1)}
      />
    </View>
  );
};


const SettingsScreen = ({ navigation }) => {
  const toast = useToast();
  const context = useContext(AppContext);

  const [serviceType, setServiceType] = useState(0);
  const [collectType, setCollectType] = useState(0);
  const [appOpenBehaviour, setAppOpenBehaviour] = useState(0);

  useEffect(() => {
    const getAppOpenBehaviour = async () => {
      const type = await DataController.getAppOpenBehaviour();
    };

    const getCollectType = async () => {
      const type = await DataController.getCollectType();
    };

    const getServiceType = async () => {
      const type = await DataController.getServiceType();
    };

    getAppOpenBehaviour();
    getAppOpenBehaviour();
    getServiceType();
  }, []);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <View style={styles.headerRow}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Image style={styles.menuIcon} source={images.Button_Back} />
            <Text style={styles.backButtonText} allowFontScaling={false}>
              {'Back'}
            </Text>
          </TouchableOpacity>
        </View>
      ),
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle} allowFontScaling={false}>
            {'Settings'}
          </Text>
        </View>
      ),
      headerRight: () => <View />,
      headerTitleAlign: 'center',
      headerStyle: {
        backgroundColor: colors.grey20,
        shadowRadius: 0,
        shadowOffset: {
          height: 0,
        },
        borderBottomWidth: 0.5,
        borderBottomColor: colors.grey50,
        height: Platform.OS === 'web' ? 44 : undefined,
      },
      headerTitleStyle: {
        justifyContent: 'center'
      },
    });
  }, [navigation]);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollViewContainer}>
      <FontController />
      <ServiceType />
      {/* <CollectType /> */}
      <OpeningType />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.accentWhite,
    flex: 1,
    height: '100%',
  },
  backButton: {
    flexDirection: 'row',
    marginStart: 4,
    alignItems: 'center',
  },
  backButtonText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    color: colors.primary,
    marginStart: -5,
  },
  scrollViewContainer: {
    width: '100%',
    maxWidth: 1200,
    paddingTop: 20,
    paddingHorizontal: Dimensions.get('window').width > 700 ? 30 : 15,
    paddingBottom: 54,
    flex: 1,
    flexGrow: 1,
    alignSelf: 'center'
  },
  headerRow: {
    flexDirection: 'row',
  },
  buttonMenu: {
    width: 44,
    height: 44,
    marginStart: 4,
  },
  menuIcon: {
    width: 44,
    height: 44,
    tintColor: colors.primary,
  },
  headerTitleContainer: {
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    color: colors.primary,
  },
  header: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: 0.2,
    color: colors.grey70,
    marginBottom: 4,
  },
  checkboxButton: {
    height: 60,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  checkboxText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    textAlign: 'center',
    color: colors.defaultText,
  },
  checkboxIcon: {
    height: 44,
    width: 44,
  },
  checkboxSeparator: {
    height: 1,
    backgroundColor: colors.grey50,
  },
  uncheckedIconContainer: {
    height: 44,
    width: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uncheckedIcon: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderRadius: 100,
    borderColor: colors.grey50
  },
  settingContainer: {
    marginTop: 24,
  },
  fontOuterContainer: {
    backgroundColor: colors.accentWhite,
    borderColor: colors.grey40,
    borderWidth: 1,
    borderRadius: 4,
    marginTop: 4,
    marginBottom: 8,
  },
  fontContainer: {
    flexDirection: 'row',
    height: 44,
  },
  fontButton: {
    width: 100,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fontIcon: {
    height: 44,
    width: 44,
  },
  fontSizeContainer: {
    flex: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.grey40,
    justifyContent: 'center',
  },
  fontSizeText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: 0,
    textAlign: 'center',
    color: colors.grey70,
  },
  resetButtonContainer: {
    borderTopColor: colors.grey40,
    borderTopWidth: 1,
  },
  resetButton: {
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  resetButtonText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: 0.1,
    color: colors.defaultText,
  },
  textPreviewContainer: {
    backgroundColor: colors.accentWhite,
    borderColor: colors.grey40,
    borderWidth: 1,
    borderRadius: 4,
    marginTop: 4,
    padding: 12,
  },
  textPreview: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 16,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 22,
    letterSpacing: -0.1,
    color: colors.defaultText,
  },
});

export default SettingsScreen;
